import { Paintbrush, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '../../../components/ui/button.js';
import { Input } from '../../../components/ui/input.js';
import { Label } from '../../../components/ui/label.js';
import { useBranding, useUpdateBranding, type BrandingTheme } from '../../common/hooks.js';

/**
 * Per-license branding editor — logo URL + 5 themable CSS-variable
 * colours. Mirrors the legacy
 * `wf-audit/src/slices/user/components/brandingSettings/BrandingSettings.tsx`
 * with the colour-picker UX simplified to native `<input type="color">`
 * (lazily falls back to a text field for hex / HSL strings the legacy
 * accepted). No file upload — logos live as URLs against the operator-
 * managed assets bucket today.
 *
 * Hidden when `useBranding()` is still loading the initial fetch so the
 * form doesn't flash empty values; admin role gate is left to the page
 * to enforce (this component is operator-targeted but renders for any
 * caller).
 */

const FIELDS: ReadonlyArray<{ key: keyof BrandingTheme; label: string; hint: string }> = [
  { key: 'primary', label: 'Primary', hint: 'Buttons, links, focus rings' },
  { key: 'sidebar', label: 'Sidebar', hint: 'Left navigation background' },
  { key: 'accent', label: 'Accent', hint: 'Highlights and emphasis' },
  { key: 'gradientStart', label: 'Gradient start', hint: 'Iframe shell top tint' },
  { key: 'gradientEnd', label: 'Gradient end', hint: 'Iframe shell bottom tint' },
];

export function BrandingSettings(): JSX.Element {
  const { data: branding, isLoading } = useBranding();
  const { update, isPending, error } = useUpdateBranding();

  const [logoUrl, setLogoUrl] = useState('');
  const [theme, setTheme] = useState<BrandingTheme>({});

  // Sync local form state with the loaded branding once it lands.
  // useEffect (not useMemo) so subsequent saves don't blow away local
  // edits; the mutation's onSuccess invalidates the query which re-runs
  // this effect with the freshly-saved values.
  useEffect(() => {
    if (!branding) return;
    setLogoUrl(branding.logoUrl ?? '');
    const t = (branding.themeJson ?? {}) as BrandingTheme;
    setTheme({
      primary: t.primary ?? '',
      sidebar: t.sidebar ?? '',
      accent: t.accent ?? '',
      gradientStart: t.gradientStart ?? '',
      gradientEnd: t.gradientEnd ?? '',
    });
  }, [branding]);

  const onSave = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    const trimmedLogo = logoUrl.trim();
    // Strip empty values so the bundle stays compact — `themeJson: {}`
    // means "no overrides" and the SPA falls back to default theme.
    const nextTheme: BrandingTheme = {};
    for (const f of FIELDS) {
      const v = theme[f.key]?.trim() ?? '';
      if (v) nextTheme[f.key] = v;
    }
    await update({
      logoUrl: trimmedLogo === '' ? null : trimmedLogo,
      themeJson: nextTheme,
    });
  };

  if (isLoading && !branding) {
    return (
      <section className="rounded-md border border-foreground/10 bg-foreground/[0.02] p-4">
        <header className="mb-3 flex items-center gap-2 text-sm font-medium text-foreground/80">
          <Paintbrush className="h-4 w-4" /> Branding
        </header>
        <div className="h-32 animate-pulse rounded bg-foreground/5" />
      </section>
    );
  }

  return (
    <section className="rounded-md border border-foreground/10 bg-foreground/[0.02] p-4">
      <header className="mb-3 flex items-center gap-2 text-sm font-medium text-foreground/80">
        <Paintbrush className="h-4 w-4" /> Branding
      </header>
      <p className="mb-4 text-xs text-foreground/50">
        Per-license logo + theme colours. Applies to every signed-in user on this license.
      </p>

      <form onSubmit={onSave} className="space-y-4">
        <div>
          <Label className="mb-1 block text-sm text-foreground/80" htmlFor="branding-logo-url">
            Logo URL
          </Label>
          <Input
            id="branding-logo-url"
            type="url"
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
            placeholder="https://…/logo.svg"
            className="border-foreground/15 bg-foreground/[0.04] text-foreground placeholder:text-foreground/30"
          />
          <p className="mt-1 text-xs text-foreground/40">
            Public HTTPS URL of an SVG or PNG. Leave blank to use the default WorkFocus logo.
          </p>
          {logoUrl.trim() ? (
            <div className="mt-2 flex h-16 items-center rounded-md border border-foreground/10 bg-foreground/[0.03] p-2">
              <img
                src={logoUrl.trim()}
                alt="Logo preview"
                className="max-h-full max-w-[200px] object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          ) : null}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {FIELDS.map(({ key, label, hint }) => {
            const value = theme[key] ?? '';
            const inputId = `branding-color-${key}`;
            // <input type="color"> requires `#rrggbb`; fall back to
            // text input when the stored value is something else (HSL,
            // var(...), etc.). Keep both visible so power-users with
            // CSS-variable preferences can type freely.
            const isHex = /^#[0-9a-fA-F]{6}$/.test(value);
            return (
              <div key={key}>
                <Label className="mb-1 block text-sm text-foreground/80" htmlFor={inputId}>
                  {label}
                </Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={isHex ? value : '#3b82f6'}
                    onChange={(e) => setTheme({ ...theme, [key]: e.target.value })}
                    className="h-9 w-12 cursor-pointer rounded border border-foreground/15 bg-transparent"
                    title={`${label} colour picker`}
                  />
                  <Input
                    id={inputId}
                    value={value}
                    onChange={(e) => setTheme({ ...theme, [key]: e.target.value })}
                    placeholder="#3b82f6 or hsl(217 91% 60%)"
                    className="flex-1 border-foreground/15 bg-foreground/[0.04] font-mono text-xs text-foreground placeholder:text-foreground/30"
                  />
                </div>
                <p className="mt-1 text-xs text-foreground/40">{hint}</p>
              </div>
            );
          })}
        </div>

        {error ? (
          <div className="rounded-md border border-rose-400/30 bg-rose-500/10 p-2 text-xs text-rose-800 dark:text-rose-200">
            {error.message}
          </div>
        ) : null}

        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={isPending}
            className="bg-[color:var(--brand-yellow)] text-[color:var(--gradient-end)] hover:opacity-90"
          >
            <Save className="mr-1.5 h-4 w-4" />
            {isPending ? 'Saving…' : 'Save branding'}
          </Button>
        </div>
      </form>
    </section>
  );
}
